import { inject, Injectable, NgZone } from '@angular/core';

import { SvcDialogService } from '@solvace/component/svc-dialogs';
import { WINDOW } from '../tokens/window.token';
import { TranslocoService } from '@ngneat/transloco';
import { AUTH_SESSION_CHANNEL, AUTH_SESSION_MESSAGE_TYPE, AuthSessionMessage } from './auth-session-channel.constants';
import { getOrCreateTabId } from './auth-tab-id.util';

@Injectable({
  providedIn: 'root',
})
export class CheckChangedAuthUserAndReloadService {
  readonly #window = inject(WINDOW);
  readonly #dialogService = inject(SvcDialogService);
  readonly #ngZone = inject(NgZone);
  readonly #transloco = inject(TranslocoService);

  readonly #channelSupported = typeof BroadcastChannel !== 'undefined';
  readonly #tabId = getOrCreateTabId(this.#window);

  readonly #authenticatedUserKey = 'authenticatedUserSub';
  readonly #storageListener = (event: StorageEvent) => this.#handleStorageEvent(event);
  readonly #channelListener = (event: MessageEvent<AuthSessionMessage>) => this.#handleChannelEvent(event);
  readonly #visibilityChangeListener = () => this.#handleVisibilityChange();
  readonly #autoReloadDelayMs = 10_000;

  #lastKnownAuthenticatedUserSub: string | null = null;
  #userChangeModalOpen = false;
  #initialized = false;
  #autoReloadTimeoutId: number | null = null;
  #waitingForVisibility = false;
  #broadcastChannel: BroadcastChannel | null = null;


  public initialize(): void {
    if (this.#initialized || !this.#window) return;
    this.#initialized = true;
    this.#lastKnownAuthenticatedUserSub = this.#getStoredAuthenticatedUserSub();

    // Always keep storage listener as a backup; dedup is handled by lastKnown check.
    this.#window.addEventListener('storage', this.#storageListener);

    if (this.#channelSupported) {
      try {
        this.#broadcastChannel = new BroadcastChannel(AUTH_SESSION_CHANNEL);
        this.#broadcastChannel.addEventListener('message', this.#channelListener);
      } catch {
        // If BroadcastChannel fails, fallback storage listener stays active.
      }
    }
  }

  #handleStorageEvent(event: StorageEvent): void {
    if (!event || event.storageArea !== this.#window?.localStorage) return;

    if (event.key === this.#authenticatedUserKey) {
      this.#handleAuthenticatedUserChange(event.newValue);
      return;
    }

    if (event.key !== null) return;

    // localStorage.clear() triggers a null key for every listener; verify if our entry changed.

    const currentStoredUser = this.#getStoredAuthenticatedUserSub();

    this.#handleAuthenticatedUserChange(currentStoredUser);
  }

  #handleChannelEvent(event: MessageEvent<AuthSessionMessage>): void {
    const message = event?.data;
    if (!message || message.type !== AUTH_SESSION_MESSAGE_TYPE) return;

    if (message.tabId && message.tabId === this.#tabId) return;

    this.#handleAuthenticatedUserChange(message.userSub ?? null);
  }

  #handleAuthenticatedUserChange(newUserSub: string | null): void {
    if (newUserSub === this.#lastKnownAuthenticatedUserSub) return;

    this.#lastKnownAuthenticatedUserSub = newUserSub;

    if (this.#userChangeModalOpen) return;
    this.#userChangeModalOpen = true;

    this.#ngZone.run(() => {
      this.#dialogService
        .openAlert(this.#transloco.translate('Houve uma mudança de usuário, e essa tela será recarregada.'), {
          title: this.#transloco.translate('Mudança de usuário'),
          buttonText: 'OK',
        })
        .subscribe(() => this.#handleDialogClosed());

      this.#startAutoReloadCountdownWhenVisible();
    });
  }

  #getStoredAuthenticatedUserSub(): string | null {
    try {
      return this.#window?.localStorage?.getItem(this.#authenticatedUserKey) ?? null;
    } catch {
      return null;
    }
  }

  #handleDialogClosed(): void {
    this.#clearAutoReloadCountdown();
    this.#clearVisibilityListener();
    this.#userChangeModalOpen = false;
    this.#reloadPage();
  }

  #startAutoReloadCountdown(): void {
    if (!this.#window) return;
    this.#clearAutoReloadCountdown();
    this.#autoReloadTimeoutId = this.#window.setTimeout(() => {
      this.#ngZone.run(() => this.#handleAutoReloadTimeout());
    }, this.#autoReloadDelayMs);
  }

  #handleAutoReloadTimeout(): void {
    if (!this.#userChangeModalOpen) return;
    this.#userChangeModalOpen = false;
    this.#clearAutoReloadCountdown();
    this.#clearVisibilityListener();
    this.#reloadPage();
  }

  #clearAutoReloadCountdown(): void {
    if (this.#autoReloadTimeoutId === null || !this.#window) return;
    this.#window.clearTimeout(this.#autoReloadTimeoutId);
    this.#autoReloadTimeoutId = null;
  }

  #startAutoReloadCountdownWhenVisible(): void {
    if (!this.#window) return;

    if (this.#isVisible()) {
      this.#startAutoReloadCountdown();
      return;
    }

    if (this.#waitingForVisibility) return;

    this.#waitingForVisibility = true;
    this.#window.addEventListener('visibilitychange', this.#visibilityChangeListener);
  }

  #handleVisibilityChange(): void {
    if (!this.#window) return;

    if (!this.#isVisible()) return;

    this.#clearVisibilityListener();
    this.#startAutoReloadCountdown();
  }

  #clearVisibilityListener(): void {
    if (!this.#window || !this.#waitingForVisibility) return;

    this.#window.removeEventListener('visibilitychange', this.#visibilityChangeListener);
    this.#waitingForVisibility = false;
  }

  #isVisible(): boolean {
    const visibilityState = this.#window?.document?.visibilityState;
    return visibilityState === undefined || visibilityState === 'visible';
  }

  #reloadPage(): void {
    this.#window?.location.reload();
  }
}
